import { IProduct } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import {
  recalculateValueFromInput,
  type ICalculateNewQuantity,
} from '../../utils/cart/recalculateValueFromInput'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAddItemToCart from './useAddItemToCart'
import useRemoveItemFromCart from './useRemoveItemFromCart'

interface IAdjustProductQuantity
  extends Pick<
    ICalculateNewQuantity,
    'originalValue' | 'inRangeValue' | 'jumpQty' | 'resultFixed' | 'resultValue' | 'nextValidValue' | 'prevValidValue' | 'ruleInvalidatedBy' | 'isValueOutOfRange'
  > {
  product: IProduct
}

export const useAdjustProductQuantity = () => {
  const { addItemAction } = useAddItemToCart()
  const { removeItemAction } = useRemoveItemFromCart()
  const dispatch = useAppDispatch()

  const freegoodsAsSubitem = useAppSelector(
    (state) => !!state.defaultSlice.config?.options?.freegoodsAsSubitem,
  )

  const autoAdd = useAppSelector(
    (state) =>
      !!state.defaultSlice.sessionData?.configuration?.promotions?.autoAdd,
  )

  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyPrefix || '',
  )

  const updateQuantityMessage = useAppSelector(
    (state) => state.defaultSlice.config?.texts?.cart.updateQuantityMessage,
  )

  const quantityAdjustment = useAppSelector(
    (state) => state.defaultSlice.sessionData?.configuration?.cart?.quantityAdjustment,
  )

  // Read from config rather than taken as an action argument: an SCS override of
  // counter.onInputChange replaces the whole cinnamonActions array, which would drop
  // the argument while the Counter prop stayed enabled and leave the two halves of
  // this flag disagreeing. Compared strictly so a stringy config value cannot enable
  // a destructive path.
  const allowTypedZero = useAppSelector(
    (state) =>
      state.defaultSlice.config?.globals?.counter?.allowTypedZero === true,
  )

  const adjustProductQuantity = (
    args: IAdjustProductQuantity,
  ): void | number => {
    const {
      product,
      product: { quantity: value, promotionalQty, minQtyAllowed, minQty },
      resultFixed,
      resultValue,
      nextValidValue,
      prevValidValue,
      ruleInvalidatedBy,
      ...restArgs
    } = args

    const newValue = recalculateValueFromInput({
      ...restArgs,
      resultValue,
      resultFixed,
      promotionalQty,
      minQtyAllowed,
      freegoodsAsSubitem,
      autoAdd,
      minQty,
      productQuantity: value,
      quantityAdjustment,
      nextValidValue,
      prevValidValue,
      ruleInvalidatedBy,
      allowTypedZero,
    })

    if (newValue !== args.inRangeValue && !resultFixed) {
      dispatch(
        addAlert({
          message: updateQuantityMessage,
          showIcon: true,
          severity: 'info',
        }),
      )
    }

    if (product.quantity === newValue) {
      return newValue
    }
    if (product.quantity > newValue) {
      removeItemAction({
        product,
        value,
        newValue,
        currency,
      })
    } else {
      addItemAction({
        product,
        value,
        newValue,
        resultFixed,
      })
    }
  }

  return {
    adjustProductQuantity,
  }
}

export default useAdjustProductQuantity
